import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMyRegistrations } from "../../hooks/useMyRegistrations";
import MyRegistrationCard from "../MyRegistrationCard/MyRegistrationCard";
import { MyRegistrationItem } from "../../api/getMyRegistrations/types";
import { Link } from "react-router-dom";
import permissionsService from "@/services/permissionsService";

function RegistrationList({
  items,
  emptyMessage,
  canManageRegistrations,
  onCancelSuccess,
}: {
  items: MyRegistrationItem[];
  emptyMessage: React.ReactNode;
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
}) {
  if (items.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((r) => (
        <MyRegistrationCard
          canManageRegistrations={canManageRegistrations}
          key={r.sessionUuid}
          onCancelSuccess={onCancelSuccess}
          registration={r}
        />
      ))}
    </div>
  );
}

const MyRegistrationsPage: React.FC = () => {
  const { upcoming, past, loading, error, refetch } = useMyRegistrations();
  const { hasPermission } = permissionsService();
  const canManageRegistrations = hasPermission("event-registration");

  return (
    <MainPageWrapper loading={loading} title="My Registrations">
      {error && (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      )}

      {!error && !loading && (
        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">
              Upcoming{upcoming.length > 0 ? ` (${upcoming.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="past">
              Past{past.length > 0 ? ` (${past.length})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            <RegistrationList
              canManageRegistrations={canManageRegistrations}
              emptyMessage={
                <>
                  No upcoming registrations.{" "}
                  <Link
                    className="text-gray-900 dark:text-white underline"
                    to="/event-registration/events"
                  >
                    Browse events
                  </Link>
                </>
              }
              items={upcoming}
              onCancelSuccess={refetch}
            />
          </TabsContent>

          <TabsContent value="past">
            <RegistrationList
              canManageRegistrations={canManageRegistrations}
              emptyMessage="No past registrations."
              items={past}
              onCancelSuccess={refetch}
            />
          </TabsContent>
        </Tabs>
      )}
    </MainPageWrapper>
  );
};

export default MyRegistrationsPage;
